import { useSearchParams } from 'react-router-dom';

import { useFindLearningJourneyItemQueryRemote } from '@/store/v2';
import { useCurrentUser } from '@/store/currentUser';
import { isObjectIdCollegialJourney } from '@/utils/analyticObjects';

export const PARENT_JOURNEY_ID_SEARCH_KEY = 'parentJourneyId';

export const useParentJourneyIdFromSearchParams = (): number | undefined => {
  const [searchParams] = useSearchParams();

  const value = searchParams.get(PARENT_JOURNEY_ID_SEARCH_KEY);

  if (!value || isNaN(Number(value))) {
    return;
  }

  return Number(value);
};

export const useLearningJourneyBreadcrumbs = (options: {
  id: number;
  isProduct?: boolean;
}): {
  journeyId: number;
  journeyTitle: string;
  currentLearningObjectId?: string;
  parentSectionId?: number;
  parentSectionURN?: string;
} | null => {
  const resourceType = options?.isProduct ? 'linked-content' : 'learning';
  const curationURN = `urn:collegial:self:${resourceType}:${options.id}`;

  const parentJourneyId = useParentJourneyIdFromSearchParams();
  const { user } = useCurrentUser();

  const { data: learningJourneyItem } = useFindLearningJourneyItemQueryRemote({
    variables: {
      filter: {
        itemURN: curationURN,
        journeyId: parentJourneyId,
        userId: user?.id ?? 0,
      },
    },
    skip: !options?.id || !curationURN || !user?.id,
  });

  const parentJourney = learningJourneyItem?.findLearningJourneyItem?.[0]?.breadcrumbs.find((breadcrumb) =>
    isObjectIdCollegialJourney(breadcrumb.urn),
  );

  const parentSection = learningJourneyItem?.findLearningJourneyItem?.[0]?.breadcrumbs.find(
    (breadcrumb) => !isObjectIdCollegialJourney(breadcrumb.urn),
  );

  if (!parentJourney) return null;

  const targetParentJourneyId = Number(parentJourney?.urn.split(':').pop());
  const targetParentSectionId = Number(parentSection?.urn.split(':').pop());

  if (Number.isNaN(targetParentJourneyId)) return null;

  const currentLearningObjectId = learningJourneyItem?.findLearningJourneyItem?.[0]?.learningItemURNV2;

  return {
    journeyId: targetParentJourneyId,
    journeyTitle: parentJourney?.content?.title ?? '',
    currentLearningObjectId,
    parentSectionId: targetParentSectionId,
    parentSectionURN: parentSection?.urn,
  };
};
